import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  FileDown, Search, CheckCircle, Clock, ChevronDown, ChevronRight,
  FileText, File, ScrollText, Camera, FolderOpen, Upload, Image, Square, Cpu,
  Settings, X, Plus, Trash2,
} from 'lucide-react';

const th = { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035' };
const td = { padding:'12px 14px', fontSize:13, color:'#cbd5e1', borderBottom:'1px solid #0f1117' };
const linkStyle = { background:'none', border:'none', color:'#e2e8f0', fontWeight:700, cursor:'pointer', padding:0, fontSize:'inherit', textDecoration:'underline', textDecorationColor:'rgba(226,232,240,0.3)', textUnderlineOffset:2 };

const btn = (c = '#f6c90e', disabled = false) => ({
  display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:7,
  background: disabled ? '#1a2035' : c + '22',
  color: disabled ? '#475569' : c,
  fontSize:12, fontWeight:600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  border:'1px solid ' + (disabled ? '#2d3748' : c + '44'),
  whiteSpace:'nowrap', textDecoration:'none',
});

/* ---- PropostaModal (4 etapas: Kit -> Valor -> Forma de Pag. -> Análise) — até 3 kits ---- */
function PropostaModal({ lead, gerarUrl, downloadPrefix, fetchLeads, view, onClose }) {
  const { isChefe } = useAuth();
  const fmtBRL = (v) => Number(v).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
  const MAX_KITS = 3;

  // kit state
  const [kits, setKits]             = useState([]);
  const [kitsSearch, setKitsSearch] = useState('');

  const candidatosIniciais = (lead.kits_propostos && lead.kits_propostos.length > 0)
    ? lead.kits_propostos.map(c => ({ id: c.kit_id, equipamento: c.kit_equipamento, marca: c.kit_marca, geracao: c.kit_geracao, valor: c.kit_valor, placas_w: c.kit_placas_w, quantidade: c.kit_quantidade, qtd_inversores: c.kit_qtd_inversores }))
    : (lead.kit_id ? [{ id: lead.kit_id, equipamento: lead.kit_equipamento, marca: lead.kit_marca, geracao: lead.kit_geracao, valor: lead.kit_valor, placas_w: lead.kit_placas_w, quantidade: lead.kit_quantidade, qtd_inversores: lead.kit_qtd_inversores }] : []);
  const valoresIniciais = {};
  (lead.kits_propostos || []).forEach(c => {
    if (c.valor_proposto) valoresIniciais[c.kit_id] = String(Number(c.valor_proposto).toFixed(2)).replace('.', ',');
  });
  if (Object.keys(valoresIniciais).length === 0 && lead.valor_total_venda && lead.kit_id) {
    valoresIniciais[lead.kit_id] = String(Number(lead.valor_total_venda).toFixed(2)).replace('.', ',');
  }

  const [selectedKits, setSelectedKits]   = useState(candidatosIniciais);
  const [valoresPorKit, setValoresPorKit] = useState(valoresIniciais);

  // forma state
  const [forma, setForma] = useState(lead.forma_pagamento || '');
  // steps: 1=Kit, 2=Valor, 3=Forma, 4=Análise
  const [step, setStep] = useState(1);

  // step 4: análise financeira
  const [custoSemStr, setCustoSemStr]     = useState('');
  const [custoComStr, setCustoComStr]     = useState('');
  const [reajusteStr, setReajusteStr]     = useState('10');
  const [gerando, setGerando] = useState(false);
  const [coeficientes, setCoeficientes] = useState([]);
  const [selectedParcela, setSelectedParcela] = useState(
    lead.cartao_parcelas ? { parcela: lead.cartao_parcelas, coeficiente: null, cartao_valor_parcela: lead.cartao_valor_parcela } : null
  );

  const comissaoPct = lead.vendedor_comissao_pct || 0;

  const valorNumFor = (kitId) => {
    const s = valoresPorKit[kitId] || '';
    return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
  };

  useEffect(() => {
    api.get('/kits/').then(r => setKits(r.data)).catch(() => {});
    api.get('/coeficientes-parcela/').then(r => setCoeficientes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Ao entrar nos kits, pré-popula valor sugerido (valor de tabela) quando ainda vazio
    setValoresPorKit(prev => {
      const next = { ...prev };
      selectedKits.forEach(k => {
        if (!next[k.id] && k.valor) next[k.id] = Number(k.valor).toFixed(2).replace('.', ',');
      });
      return next;
    });
  }, [selectedKits]);

  const kitsFiltrados = kitsSearch.trim()
    ? kits.filter(k => k.equipamento.toLowerCase().includes(kitsSearch.toLowerCase()) || String(k.geracao).includes(kitsSearch))
    : kits;

  const toggleKit = (k) => {
    setSelectedKits(prev => {
      const exists = prev.find(p => p.id === k.id);
      if (exists) return prev.filter(p => p.id !== k.id);
      if (prev.length >= MAX_KITS) { alert(`Você pode selecionar no máximo ${MAX_KITS} kits.`); return prev; }
      return [...prev, k];
    });
  };

  // step validation
  const step1Ok = selectedKits.length >= 1 && selectedKits.length <= MAX_KITS;
  const step2Ok = selectedKits.every(k => valorNumFor(k.id) > 0);
  const step3Ok = forma && (forma !== 'Cartão' ? true : selectedParcela != null);
  const custoSemNum = parseFloat(custoSemStr.replace(/\./g, '').replace(',', '.')) || 0;
  const custoComNum = parseFloat(custoComStr.replace(/\./g, '').replace(',', '.')) || 0;
  const reajusteNum = parseFloat(reajusteStr.replace(',', '.')) || 10;
  const step4Ok = custoSemNum > 0 && custoComNum > 0;

  const handleGerar = async () => {
    setGerando(true);
    // Pré-abrir janela ANTES do await — iOS Safari bloqueia window.open em contexto assíncrono.
    // Abrir aqui garante que ainda estamos dentro do gesto do usuário (clique no botão).
    const win = window.open('', '_blank');
    try {
      const patch = { forma_pagamento: forma };
      if (forma === 'Cartão' && selectedParcela) {
        const valorBase = valorNumFor(selectedKits[0].id);
        const coef = parseFloat(selectedParcela.coeficiente);
        const totalComTaxa = parseFloat((valorBase * coef).toFixed(2));
        patch.valor_total_com_taxa = totalComTaxa;
        patch.cartao_parcelas = selectedParcela.parcela;
        patch.cartao_valor_parcela = parseFloat((totalComTaxa / selectedParcela.parcela).toFixed(2));
      }
      await api.patch('/leads/' + lead.lead_id + '/', patch);
      const kitsBody = selectedKits.map(k => ({ kit_id: k.id, valor: valorNumFor(k.id) }));
      const extraParams = {
        kits: kitsBody,
        custo_sem: custoSemNum,
        custo_com: custoComNum,
        reajuste_anual: reajusteNum,
      };
      const resp = await api.post(gerarUrl + lead.lead_id + '/', extraParams, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type:'application/pdf' }));
      if (win && !win.closed) {
        // Redirecionar a janela já aberta para o PDF (funciona no iOS e desktop)
        win.location.href = url;
      } else {
        // Popup bloqueado (ex: desktop com bloqueador) — fallback com download direto
        const a = document.createElement('a'); a.href = url; a.download = downloadPrefix + '_' + lead.lead_id + '.pdf'; a.click();
      }
      // Aguardar antes de revogar — iOS precisa de tempo para ler o blob
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      fetchLeads(view);
      onClose();
    } catch (e) {
      if (win && !win.closed) win.close();
      let msg = e.message || 'tente novamente';
      if (e.response) {
        let data = e.response.data;
        if (data instanceof Blob) {
          try { data = JSON.parse(await data.text()); } catch (_) { data = null; }
        }
        msg = data?.error || data?.erro || msg;
      }
      alert('Erro ao gerar proposta: ' + msg);
    } finally { setGerando(false); }
  };

  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' };
  const box     = { background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:24, width:620, maxWidth:'95vw', maxHeight:'88vh', display:'flex', flexDirection:'column' };
  const stepBtn = (active) => ({ width:28, height:28, borderRadius:'50%', border:'none', cursor:'default', fontWeight:700, fontSize:13, background: active ? '#f6c90e' : '#1a2035', color: active ? '#080c14' : '#475569', display:'inline-flex', alignItems:'center', justifyContent:'center' });
  const nextBtn = (ok) => ({ background: ok ? '#f6c90e' : '#1a2035', color: ok ? '#080c14' : '#475569', border:'none', borderRadius:8, padding:'9px 22px', fontWeight:700, fontSize:13, cursor: ok ? 'pointer' : 'not-allowed' });
  const inputS  = { background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'9px 13px', color:'#e2e8f0', fontSize:14, outline:'none', width:'100%', boxSizing:'border-box' };
  const lbl     = { fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:6, display:'block' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        {/* header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#f6c90e' }}>Gerar Proposta</div>
            <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>{lead.cliente}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', display:'flex' }}><X size={18}/></button>
        </div>

        {/* steps indicator */}
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:20 }}>
          {[['1','Kit Solar'], ['2','Valor'], ['3','Pagamento'], ['4','Análise']].map(([n, label], i) => (
            <React.Fragment key={n}>
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <div style={stepBtn(step === i+1)}>{n}</div>
                <span style={{ fontSize:11, color: step === i+1 ? '#e2e8f0' : '#475569', fontWeight: step === i+1 ? 600 : 400 }}>{label}</span>
              </div>
              {i < 3 && <div style={{ flex:1, height:1, background:'#1a2035' }}/>}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: KIT (até 3 selecionáveis) ──────────────────────── */}
        {step === 1 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:10 }}>Escolha de 1 a {MAX_KITS} kits solares para esta proposta:</div>
            {selectedKits.length > 0 && (
              <div style={{ background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:8,
                            padding:'9px 14px', marginBottom:10, fontSize:13, color:'#4ade80', fontWeight:600 }}>
                ✅ {selectedKits.length} {selectedKits.length === 1 ? 'kit selecionado' : 'kits selecionados'}
              </div>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:8, background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'7px 12px', marginBottom:10 }}>
              <Search size={13} color="#475569"/>
              <input style={{ background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 }}
                placeholder="Buscar equipamento ou geracao..." value={kitsSearch} onChange={e => setKitsSearch(e.target.value)} autoFocus />
            </div>
            <div style={{ overflowY:'auto', overflowX:'auto', maxHeight:300, border:'1px solid #1a2035', borderRadius:8 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ position:'sticky', top:0, background:'#0e1420', zIndex:10 }}>
                    {['Marca','Equipamento','Qtd Inversores','Ger. (kWh)','Placas','Pot. Total','Valor',''].map(h => (
                      <th key={h} style={{ padding:'8px 10px', fontSize:10, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035', textAlign:'left', background:'#0e1420' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kitsFiltrados.map(k => {
                    const potTotal = ((k.placas_w * k.quantidade) / 1000).toFixed(2);
                    const idx = selectedKits.findIndex(p => p.id === k.id);
                    const isSelected = idx !== -1;
                    return (
                      <tr key={k.id} style={{ background: isSelected ? 'rgba(246,201,14,0.1)' : 'transparent', cursor:'pointer' }}
                        onClick={() => toggleKit(k)}>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#a78bfa', fontWeight:600 }}>{k.marca || '—'}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#e2e8f0', fontWeight:600 }}>
                          {isSelected && <span style={{ color:'#f6c90e', marginRight:6 }}>Opção {idx + 1}</span>}
                          {k.equipamento}
                        </td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#94a3b8' }}>{k.qtd_inversores ?? '—'}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#f6c90e', fontWeight:700 }}>{k.geracao}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#94a3b8' }}>{k.quantidade}x {k.placas_w}W</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#60a5fa', fontWeight:600 }}>{potTotal} kWp</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#4ade80', fontWeight:600 }}>{fmtBRL(k.valor)}</td>
                        <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117' }}>
                          <button onClick={e => { e.stopPropagation(); toggleKit(k); }}
                            style={{ background: isSelected ? 'transparent' : '#f6c90e', color: isSelected ? '#f87171' : '#080c14',
                                     border: isSelected ? '1px solid rgba(248,113,113,0.4)' : 'none', borderRadius:6, padding:'5px 12px',
                                     fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                            {isSelected ? 'Remover' : 'Escolher Este'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {kitsFiltrados.length === 0 && (
                    <tr><td colSpan={8} style={{ padding:24, textAlign:'center', color:'#475569' }}>Nenhum kit encontrado</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── STEP 2: VALOR (um campo por kit selecionado) ───────────── */}
        {step === 2 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            {selectedKits.map((k, i) => {
              const kitValor = k.valor ? Number(k.valor) : 0;
              const valorNum = valorNumFor(k.id);
              const limiteDesconto = kitValor > 0 ? kitValor * 0.95 : 0;
              const temDesconto = !isChefe() && kitValor > 0 && valorNum > 0 && valorNum < limiteDesconto;
              const pctDesconto = kitValor > 0 && valorNum > 0 ? ((1 - valorNum / kitValor) * 100) : 0;
              const comissaoVal = comissaoPct > 0 ? valorNum * comissaoPct / 100 : null;
              return (
                <div key={k.id} style={{ marginBottom: i < selectedKits.length - 1 ? 22 : 0, paddingBottom: i < selectedKits.length - 1 ? 18 : 0, borderBottom: i < selectedKits.length - 1 ? '1px solid #1a2035' : 'none' }}>
                  {selectedKits.length > 1 && (
                    <div style={{ fontSize:11, color:'#f6c90e', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Opção {i + 1} — {k.equipamento}</div>
                  )}
                  {kitValor > 0 && (
                    <div style={{ fontSize:12, color:'#64748b', marginBottom:8 }}>
                      💡 Valor do kit {selectedKits.length > 1 ? i + 1 : ''}: <strong style={{ color:'#e2e8f0' }}>{fmtBRL(kitValor)}</strong>
                      {!isChefe() && <span style={{ color:'#94a3b8' }}> · Baixando abaixo de 5%, a proposta e comissão serão revisadas pelo chefe</span>}
                    </div>
                  )}
                  <label style={lbl}>Valor total da venda (R$)</label>
                  <input
                    style={{ ...inputS, fontSize:20, fontWeight:700, color: temDesconto ? '#f87171' : '#f6c90e', marginBottom:12 }}
                    type="text" placeholder="Ex: 37.555,00"
                    value={valoresPorKit[k.id] || ''}
                    onChange={e => setValoresPorKit(v => ({ ...v, [k.id]: e.target.value.replace(/[^0-9,]/g, '') }))}
                    autoFocus={i === 0}
                  />
                  {temDesconto ? (
                    <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.35)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#f87171', marginBottom:4 }}>⚠️ Desconto acima de 5%</div>
                      <div style={{ fontSize:13, color:'#fca5a5' }}>
                        O valor é <strong>{pctDesconto.toFixed(2)}%</strong> menor que o valor do kit ({fmtBRL(kitValor)}).
                      </div>
                      <div style={{ fontSize:12, color:'#94a3b8', marginTop:6 }}>
                        O PDF da proposta será gerado, mas o chefe revisará a proposta e a comissão dessa venda.
                      </div>
                    </div>
                  ) : (
                    <>
                      {comissaoVal !== null && valorNum > 0 && comissaoPct > 0 && !isChefe() && (
                        <div style={{ background:'rgba(74,222,128,0.07)', border:'1px solid rgba(74,222,128,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:12 }}>
                          <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:4 }}>Sua comissão estimada</div>
                          <div style={{ fontSize:20, fontWeight:800, color:'#4ade80' }}>{fmtBRL(comissaoVal)}</div>
                          <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{comissaoPct}% sobre {fmtBRL(valorNum)}</div>
                        </div>
                      )}
                      {!isChefe() && comissaoPct === 0 && valorNum > 0 && (
                        <div style={{ fontSize:12, color:'#475569', marginBottom:12 }}>Percentual de comissão não configurado.</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── STEP 3: FORMA DE PAGAMENTO ─────────────────────────────── */}
        {step === 3 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            <label style={lbl}>Forma de Pagamento</label>
            <select style={{ ...inputS, marginBottom:16 }} value={forma} onChange={e => { setForma(e.target.value); setSelectedParcela(null); }} autoFocus>
              <option value="">— Selecione —</option>
              <option>A Vista</option>
              <option>Financiamento</option>
              <option value="Cartão">Cartão</option>
              <option>Boleto</option>
            </select>
            {(forma === 'Cartão') && (() => {
              const valorBase = selectedKits.length > 0 ? valorNumFor(selectedKits[0].id) : 0;
              return (
              <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.18)', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:12, color:'#64748b', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>
                  Selecione a quantidade de parcelas{selectedKits.length > 1 ? ' (prévia com base na Opção 1)' : ''}:
                </div>
                {valorBase <= 0 && (
                  <div style={{ fontSize:12, color:'#ef4444', marginBottom:8 }}>Defina o valor da venda no passo anterior.</div>
                )}
                <div style={{ overflowY:'auto', overflowX:'auto', maxHeight:220, border:'1px solid #1a2035', borderRadius:8 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        {['Parcelas','Juros','Valor/parcela','Total com taxa',''].map(h => (
                          <th key={h} style={{ padding:'7px 10px', fontSize:10, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035', textAlign:'left', background:'#0e1420', position:'sticky', top:0 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {coeficientes.map(item => {
                        const coef = parseFloat(item.coeficiente);
                        const totalCom = valorBase > 0 ? valorBase * coef : 0;
                        const parcelaCom = totalCom / item.parcela;
                        const juros = ((coef - 1) * 100).toLocaleString('pt-BR', { minimumFractionDigits:2, maximumFractionDigits:2 }) + '%';
                        const isSelected = selectedParcela?.parcela === item.parcela;
                        return (
                          <tr key={item.id}
                            style={{ background: isSelected ? 'rgba(246,201,14,0.1)' : 'transparent', cursor:'pointer' }}
                            onClick={() => setSelectedParcela(item)}>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:14, fontWeight:800, color:'#f6c90e' }}>{item.parcela}x</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#60a5fa', fontWeight:700 }}>{juros}</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#f6c90e', fontWeight:700 }}>{valorBase > 0 ? fmtBRL(parcelaCom) : '—'}</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:13, color:'#4ade80', fontWeight:700 }}>{valorBase > 0 ? fmtBRL(totalCom) : '—'}</td>
                            <td style={{ padding:'8px 10px', borderBottom:'1px solid #0f1117', fontSize:12, color:'#4ade80', fontWeight:700 }}>
                              {isSelected && '✓'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {selectedParcela && valorBase > 0 && (
                  <div style={{ marginTop:10, fontSize:13, color:'#4ade80', fontWeight:600 }}>
                    {selectedParcela.parcela}x de {fmtBRL((valorBase * parseFloat(selectedParcela.coeficiente)) / selectedParcela.parcela)} &nbsp;•&nbsp; Total: {fmtBRL(valorBase * parseFloat(selectedParcela.coeficiente))}
                  </div>
                )}
              </div>
              );
            })()}
            {/* resumo */}
            <div style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:10, padding:'12px 16px', marginTop:16 }}>
              <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:8 }}>Resumo da proposta</div>
              {selectedKits.map((k, i) => (
                <div key={k.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 16px', fontSize:13, marginBottom: i < selectedKits.length - 1 ? 8 : 0 }}>
                  <div style={{ color:'#475569' }}>{selectedKits.length > 1 ? `Kit (Opção ${i + 1}):` : 'Kit:'}</div>
                  <div style={{ color:'#e2e8f0', fontWeight:600 }}>{k.equipamento || '—'}</div>
                  <div style={{ color:'#475569' }}>Qtd Inversores:</div>
                  <div style={{ color:'#e2e8f0' }}>{k.qtd_inversores ?? '—'}</div>
                  <div style={{ color:'#475569' }}>Valor:</div>
                  <div style={{ color:'#f6c90e', fontWeight:700 }}>{valorNumFor(k.id) > 0 ? fmtBRL(valorNumFor(k.id)) : '—'}</div>
                </div>
              ))}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 16px', fontSize:13, marginTop:8 }}>
                <div style={{ color:'#475569' }}>Pagamento:</div>
                <div style={{ color:'#e2e8f0' }}>{forma || '—'}</div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 4: ANÁLISE FINANCEIRA ─────────────────────────────── */}
        {step === 4 && (
          <div style={{ flex:1, minHeight:0, overflowY:'auto' }}>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
              Informe os valores de energia para calcular o retorno do investimento automaticamente.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:16 }}>
              <div>
                <label style={lbl}>Custo mensal SEM sistema (R$)</label>
                <input
                  style={{ ...inputS, fontSize:16, fontWeight:700, color:'#ef4444' }}
                  type="text" placeholder="Ex: 850,00"
                  value={custoSemStr}
                  onChange={e => setCustoSemStr(e.target.value.replace(/[^0-9,]/g, ''))}
                  autoFocus
                />
              </div>
              <div>
                <label style={lbl}>Custo mensal COM sistema (R$)</label>
                <input
                  style={{ ...inputS, fontSize:16, fontWeight:700, color:'#4ade80' }}
                  type="text" placeholder="Ex: 95,00"
                  value={custoComStr}
                  onChange={e => setCustoComStr(e.target.value.replace(/[^0-9,]/g, ''))}
                />
              </div>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={lbl}>Reajuste anual de energia (%)</label>
              <input
                style={{ ...inputS, width:160 }}
                type="text" placeholder="10"
                value={reajusteStr}
                onChange={e => setReajusteStr(e.target.value.replace(/[^0-9,.]/g, ''))}
              />
              <div style={{ fontSize:11, color:'#475569', marginTop:5 }}>Padrão: 10% ao ano (média histórica ANEEL)</div>
            </div>

            {custoSemNum > 0 && custoComNum > 0 && (
              <div style={{ background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.18)', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:10 }}>Prévia dos cálculos</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 20px', fontSize:13 }}>
                  <div style={{ color:'#475569' }}>Economia mensal:</div>
                  <div style={{ color:'#4ade80', fontWeight:700 }}>
                    {(custoSemNum - custoComNum).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}
                  </div>
                  <div style={{ color:'#475569' }}>Economia anual:</div>
                  <div style={{ color:'#4ade80', fontWeight:700 }}>
                    {((custoSemNum - custoComNum) * 12).toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}
                  </div>
                  {selectedKits.map((k, i) => {
                    const v = valorNumFor(k.id);
                    return (
                      <React.Fragment key={k.id}>
                        <div style={{ color:'#475569' }}>Payback {selectedKits.length > 1 ? `(Opção ${i + 1})` : 'estimado'}:</div>
                        <div style={{ color:'#f6c90e', fontWeight:700 }}>{v > 0 ? `≈ ${(v / ((custoSemNum - custoComNum) * 12)).toFixed(1)} anos` : '—'}</div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* footer buttons */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20, paddingTop:16, borderTop:'1px solid #1a2035' }}>
          <button onClick={step === 1 ? onClose : () => setStep(s => s-1)}
            style={{ background:'transparent', border:'1px solid #1a2035', borderRadius:8, padding:'8px 18px', color:'#64748b', fontSize:13, cursor:'pointer' }}>
            {step === 1 ? 'Cancelar' : '← Voltar'}
          </button>
          {step < 4 ? (
            <button
              style={nextBtn(step === 1 ? step1Ok : step === 2 ? step2Ok : step3Ok)}
              disabled={step === 1 ? !step1Ok : step === 2 ? !step2Ok : !step3Ok}
              onClick={() => setStep(s => s+1)}>
              Próximo →
            </button>
          ) : (
            <button
              style={{ ...nextBtn(step4Ok && !gerando), display:'flex', alignItems:'center', gap:6 }}
              disabled={!step4Ok || gerando}
              onClick={handleGerar}>
              <FileDown size={14}/>{gerando ? 'Gerando...' : 'Confirmar e Gerar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- ModeloPropostaModal ---- */
function ModeloPropostaModal({ onClose }) {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingMarca, setUploadingMarca] = useState(false);
  const [p1Nome, setP1Nome] = useState('');
  const [p1Desc, setP1Desc] = useState('');
  const [p2Nome, setP2Nome] = useState('');
  const [p2Desc, setP2Desc] = useState('');
  const p1ImgRef = useRef();
  const p2ImgRef = useRef();
  const marcaRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/modelo-proposta/');
      setDados(r.data);
      const rawImgs = r.data.imagens || [];
      const slotted = [null, null];
      rawImgs.forEach(img => {
        if (img.ordem === 0 || img.ordem === 1) slotted[img.ordem] = img;
        else if (slotted[0] === null) slotted[0] = img;
        else slotted[1] = img;
      });
      r.data.imagens = slotted;
      setP1Nome(r.data.portfolio1_nome || '');
      setP1Desc(r.data.portfolio1_descricao || '');
      setP2Nome(r.data.portfolio2_nome || '');
      setP2Desc(r.data.portfolio2_descricao || '');
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    setSaving(true);
    try {
      await api.patch('/modelo-proposta/', { portfolio1_nome: p1Nome, portfolio1_descricao: p1Desc, portfolio2_nome: p2Nome, portfolio2_descricao: p2Desc });
      alert('Modelo salvo com sucesso!');
    } catch { alert('Erro ao salvar.'); }
    finally { setSaving(false); }
  };

  const uploadImagemPortfolio = async (file, idx) => {
    setUploadingImg(true);
    try {
      const existing = dados?.imagens?.[idx];
      if (existing) await api.delete('/modelo-proposta/imagens/' + existing.id + '/');
      const form = new FormData();
      form.append('arquivo', file);
      form.append('ordem', idx);
      const r = await api.post('/modelo-proposta/imagens/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDados(prev => {
        const imgs = [...(prev.imagens || [])].filter(i => !existing || i.id !== existing.id);
        while (imgs.length < idx) imgs.push(null);
        imgs[idx] = r.data;
        return { ...prev, imagens: imgs };
      });
    } catch (e) { alert('Erro ao enviar imagem: ' + (e.response?.data?.detail || e.message || 'tente novamente')); }
    finally { setUploadingImg(false); }
  };

  const deleteImagemPortfolio = async (idx) => {
    const img = dados?.imagens?.[idx];
    if (!img || !window.confirm('Remover esta foto?')) return;
    try {
      await api.delete('/modelo-proposta/imagens/' + img.id + '/');
      setDados(prev => ({ ...prev, imagens: prev.imagens.filter(i => i.id !== img.id) }));
    } catch { alert('Erro ao remover.'); }
  };

  const uploadMarca = async (file, nome) => {
    setUploadingMarca(true);
    try {
      const form = new FormData();
      form.append('arquivo', file);
      form.append('nome', nome || '');
      const r = await api.post('/modelo-proposta/marcas/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDados(prev => ({ ...prev, marcas: [...(prev.marcas || []), r.data] }));
    } catch (e) { alert('Erro ao enviar logo: ' + (e.response?.data?.detail || e.message || 'tente novamente')); }
    finally { setUploadingMarca(false); }
  };

  const deleteMarca = async (id) => {
    if (!window.confirm('Remover esta marca?')) return;
    try {
      await api.delete('/modelo-proposta/marcas/' + id + '/');
      setDados(prev => ({ ...prev, marcas: prev.marcas.filter(m => m.id !== id) }));
    } catch { alert('Erro ao remover.'); }
  };

  const inp = { width:'100%', background:'#080c14', border:'1px solid #1a2035', borderRadius:8, color:'#e2e8f0', fontSize:13, padding:'9px 12px', outline:'none', boxSizing:'border-box' };
  const sec = { background:'#080c14', border:'1px solid #1a2035', borderRadius:10, padding:16 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:28, width:700, maxWidth:'100%', maxHeight:'90vh', display:'flex', flexDirection:'column', overflowY:'auto', gap:20 }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:18, fontWeight:700, color:'#f6c90e' }}>Modelo de Proposta</div>
            <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>Configure o template global (página 2 da proposta)</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', display:'flex' }}><X size={20}/></button>
        </div>

        {loading ? <div style={{ textAlign:'center', color:'#475569', padding:32 }}>Carregando...</div> : (
          <>
            <div style={sec}>
              <div style={{ fontSize:13, fontWeight:700, color:'#f6c90e', marginBottom:14 }}>Portfólio 1</div>
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:12, color:'#94a3b8', fontWeight:600, marginBottom:4, display:'block' }}>Nome do Projeto</label>
                <input style={inp} value={p1Nome} onChange={e => setP1Nome(e.target.value)} placeholder="Ex: Supermercado Alves e Brito" />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#94a3b8', fontWeight:600, marginBottom:4, display:'block' }}>Descrição</label>
                <textarea style={{ ...inp, minHeight:80, resize:'vertical' }} value={p1Desc}
                  onChange={e => setP1Desc(e.target.value)} placeholder="Descreva o projeto, potência, economia gerada..." />
              </div>
              <div style={{ marginTop:10 }}>
                <label style={{ fontSize:12, color:'#94a3b8', fontWeight:600, marginBottom:6, display:'block' }}>Foto do Projeto</label>
                {dados?.imagens?.[0] ? (
                  <div style={{ position:'relative', width:'100%', height:120, borderRadius:8, overflow:'hidden', border:'1px solid #1a2035', background:'#0d1117' }}>
                    <img src={dados.imagens[0].url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                      onError={e => { e.target.style.display='none'; }} />
                    <button onClick={() => deleteImagemPortfolio(0)} style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,0.7)', border:'none', borderRadius:4, color:'#f87171', cursor:'pointer', display:'flex', padding:4 }}><Trash2 size={13}/></button>
                    <div style={{ position:'absolute', bottom:6, left:8, fontSize:10, color:'#475569' }}>Clique 🗑 para remover e reenviar</div>
                  </div>
                ) : (
                  <button onClick={() => p1ImgRef.current.click()} disabled={uploadingImg}
                    style={{ width:'100%', height:72, background:'#080c14', border:'1px dashed #1a2035', borderRadius:8, color:'#475569', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontSize:13 }}>
                    <Plus size={14}/> {uploadingImg ? 'Enviando...' : 'Adicionar foto'}
                  </button>
                )}
                <input ref={p1ImgRef} type="file" accept="image/*,.svg,.ico" style={{ display:'none' }}
                  onChange={e => { if (e.target.files[0]) uploadImagemPortfolio(e.target.files[0], 0); e.target.value=''; }} />
              </div>
            </div>

            <div style={sec}>
              <div style={{ fontSize:13, fontWeight:700, color:'#f6c90e', marginBottom:14 }}>Portfólio 2</div>
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:12, color:'#94a3b8', fontWeight:600, marginBottom:4, display:'block' }}>Nome do Projeto</label>
                <input style={inp} value={p2Nome} onChange={e => setP2Nome(e.target.value)} placeholder="Ex: Arte Final Alarmes" />
              </div>
              <div>
                <label style={{ fontSize:12, color:'#94a3b8', fontWeight:600, marginBottom:4, display:'block' }}>Descrição</label>
                <textarea style={{ ...inp, minHeight:80, resize:'vertical' }} value={p2Desc}
                  onChange={e => setP2Desc(e.target.value)} placeholder="Descreva o projeto, potência, economia gerada..." />
              </div>
              <div style={{ marginTop:10 }}>
                <label style={{ fontSize:12, color:'#94a3b8', fontWeight:600, marginBottom:6, display:'block' }}>Foto do Projeto</label>
                {dados?.imagens?.[1] ? (
                  <div style={{ position:'relative', width:'100%', height:120, borderRadius:8, overflow:'hidden', border:'1px solid #1a2035', background:'#0d1117' }}>
                    <img src={dados.imagens[1].url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                      onError={e => { e.target.style.display='none'; }} />
                    <button onClick={() => deleteImagemPortfolio(1)} style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,0.7)', border:'none', borderRadius:4, color:'#f87171', cursor:'pointer', display:'flex', padding:4 }}><Trash2 size={13}/></button>
                    <div style={{ position:'absolute', bottom:6, left:8, fontSize:10, color:'#475569' }}>Clique 🗑 para remover e reenviar</div>
                  </div>
                ) : (
                  <button onClick={() => p2ImgRef.current.click()} disabled={uploadingImg}
                    style={{ width:'100%', height:72, background:'#080c14', border:'1px dashed #1a2035', borderRadius:8, color:'#475569', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontSize:13 }}>
                    <Plus size={14}/> {uploadingImg ? 'Enviando...' : 'Adicionar foto'}
                  </button>
                )}
                <input ref={p2ImgRef} type="file" accept="image/*,.svg,.ico" style={{ display:'none' }}
                  onChange={e => { if (e.target.files[0]) uploadImagemPortfolio(e.target.files[0], 1); e.target.value=''; }} />
              </div>
              <div style={{ marginTop:14, textAlign:'right' }}>
                <button onClick={salvar} disabled={saving}
                  style={{ background:'#f6c90e', color:'#080c14', border:'none', borderRadius:8, padding:'8px 20px', fontSize:13, fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Salvando...' : 'Salvar Portfólios'}
                </button>
              </div>
            </div>

            <div style={sec}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#e2e8f0' }}>Marcas Parceiras</div>
                <button onClick={() => marcaRef.current.click()} disabled={uploadingMarca} style={{ ...btn('#60a5fa', uploadingMarca), fontSize:12 }}>
                  <Plus size={13} /> {uploadingMarca ? 'Enviando...' : 'Adicionar marca'}
                </button>
                <input ref={marcaRef} type="file" accept="image/*,.svg,.ico" style={{ display:'none' }}
                  onChange={e => {
                    if (e.target.files[0]) {
                      const nome = window.prompt('Nome da marca (opcional):') || '';
                      uploadMarca(e.target.files[0], nome);
                    }
                    e.target.value='';
                  }} />
              </div>
              {(!dados?.marcas || dados.marcas.length === 0)
                ? <div style={{ color:'#475569', fontSize:13, textAlign:'center', padding:'16px 0' }}>Nenhuma marca adicionada</div>
                : <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'flex-end' }}>
                    {dados.marcas.map(m => (
                      <div key={m.id} style={{ position:'relative', textAlign:'center' }}>
                        <div style={{ width:90, height:56, borderRadius:8, overflow:'hidden', border:'1px solid #1a2035', background:'#fff' }}>
                          <img src={m.url} alt={m.nome} style={{ width:'100%', height:'100%', objectFit:'contain', padding:4 }} />
                        </div>
                        {m.nome && <div style={{ fontSize:10, color:'#64748b', marginTop:3 }}>{m.nome}</div>}
                        <button onClick={() => deleteMarca(m.id)}
                          style={{ position:'absolute', top:2, right:2, background:'rgba(0,0,0,0.7)', border:'none', borderRadius:4, color:'#f87171', cursor:'pointer', display:'flex', padding:2 }}>
                          <Trash2 size={11}/>
                        </button>
                      </div>
                    ))}
                  </div>
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- EscolherKitContratoModal: escolha de qual kit (entre os propostos) vai para o contrato ---- */
function EscolherKitContratoModal({ lead, gerando, onConfirm, onClose }) {
  const fmtBRL = (v) => Number(v).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
  const candidatos = lead.kits_propostos || [];
  const [selectedKitId, setSelectedKitId] = useState(
    lead.kit_id ? String(lead.kit_id) : (candidatos.length === 1 ? String(candidatos[0].kit_id) : '')
  );

  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' };
  const box     = { background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:24, width:480, maxWidth:'95vw', maxHeight:'88vh', display:'flex', flexDirection:'column' };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'#f6c90e' }}>📦 Confirme o Kit do Contrato</div>
            <div style={{ fontSize:12, color:'#475569', marginTop:2 }}>{lead.cliente}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', display:'flex' }}><X size={18}/></button>
        </div>

        {candidatos.length === 0 ? (
          <div style={{ fontSize:13, color:'#f87171', marginBottom:16 }}>
            Nenhum kit foi proposto para esta venda. Gere a proposta antes de gerar o contrato.
          </div>
        ) : (
          <>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:12 }}>
              Escolha qual kit proposto vai para o contrato:
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:18, overflowY:'auto', maxHeight:300 }}>
              {candidatos.map((c, idx) => {
                const isSel = String(c.kit_id) === selectedKitId;
                return (
                  <label key={c.kit_id} onClick={() => setSelectedKitId(String(c.kit_id))}
                    style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer', padding:'10px 12px',
                             borderRadius:8, border:'1px solid ' + (isSel ? 'rgba(167,139,250,0.6)' : '#1a2035'),
                             background: isSel ? 'rgba(167,139,250,0.12)' : 'transparent' }}>
                    <input type="radio" checked={isSel} onChange={() => {}}
                      style={{ accentColor:'#a78bfa', width:15, height:15, marginTop:2 }} />
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:'#e2e8f0' }}>
                        {candidatos.length > 1 ? `Opção ${idx + 1} · ` : ''}
                        {c.kit_marca ? `${c.kit_marca} ` : ''}{c.kit_equipamento || '—'}
                      </div>
                      <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>
                        {c.kit_geracao ? `${c.kit_geracao} kWh/mês · ` : ''}
                        {c.kit_qtd_inversores != null ? `${c.kit_qtd_inversores} inversor(es) · ` : ''}
                        <span style={{ color:'#f6c90e', fontWeight:600 }}>
                          {c.valor_proposto != null ? fmtBRL(c.valor_proposto) : '—'}
                        </span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ background:'none', border:'1px solid #1a2035', borderRadius:8, padding:'9px 18px', color:'#94a3b8', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancelar</button>
          <button
            disabled={!selectedKitId || gerando}
            onClick={() => onConfirm(selectedKitId)}
            style={{ background: (!selectedKitId || gerando) ? '#1a2035' : '#f6c90e', color: (!selectedKitId || gerando) ? '#475569' : '#080c14',
                     border:'none', borderRadius:8, padding:'9px 22px', fontWeight:700, fontSize:13, cursor: (!selectedKitId || gerando) ? 'not-allowed' : 'pointer' }}>
            {gerando ? 'Gerando...' : 'Gerar Contrato'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- DocSection ---- */
function DocSection({ title, icon: Icon, fetchUrl, gerarUrl, downloadPrefix, docType, readonly }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('disponivel');
  const [gerandoId, setGerandoId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [propostaModal, setPropostaModal] = useState(null);
  const [kitContratoModal, setKitContratoModal] = useState(null);
  const [uploadingSignedId, setUploadingSignedId] = useState(null);

  const fetchLeads = useCallback((v) => {
    api.get(fetchUrl + '?view=' + (v || 'disponivel')).then(r => setLeads(r.data)).catch(() => {});
  }, [fetchUrl]);

  useEffect(() => {
    if (sectionOpen && !loaded) { fetchLeads('disponivel'); setLoaded(true); }
  }, [sectionOpen, loaded, fetchLeads]);

  const switchView = (v) => { setView(v); setSearch(''); fetchLeads(v); };

  const filtered = leads.filter(l =>
    l.cliente?.toLowerCase().includes(search.toLowerCase()) ||
    l.cidade?.toLowerCase().includes(search.toLowerCase())
  );

  const gerar = async (leadId, kitId) => {
    setGerandoId(leadId);
    try {
      const body = kitId ? { kit_id: kitId } : {};
      const resp = await api.post(gerarUrl + leadId + '/', body, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type:'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = downloadPrefix + '_' + leadId + '.pdf'; a.click();
      window.URL.revokeObjectURL(url);
      fetchLeads(view);
    } catch (e) {
      alert('Erro ao gerar ' + title.toLowerCase() + ': ' + (e.response?.data?.error || e.message || 'tente novamente'));
    } finally { setGerandoId(null); }
  };

  const handleGerarClick = (lead) => {
    if (docType === 'proposta') {
      // Sempre abre o modal de 4 etapas para proposta
      setPropostaModal(lead);
    } else if (docType === 'contrato') {
      // Sempre pede para o vendedor confirmar qual kit (entre os propostos) vai para o contrato
      setKitContratoModal(lead);
    } else {
      gerar(lead.lead_id);
    }
  };

  const uploadSigned = async (leadId, file) => {
    // Validação local de tamanho: 20 MB. Previne timeout do gunicorn (120s)
    // em conexões móveis lentas, que causaria Network Error sem mensagem útil.
    const MAX_MB = 20;
    if (file.size > MAX_MB * 1024 * 1024) {
      alert(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo: ${MAX_MB} MB.`);
      return;
    }
    setUploadingSignedId(leadId);
    try {
      const form = new FormData();
      form.append('arquivo', file);
      const endpoint = docType === 'cemig'
        ? '/documentos/subir-cemig-assinado/' + leadId + '/'
        : '/documentos/subir-contrato-assinado/' + leadId + '/';
      await api.post(endpoint, form);
      fetchLeads(view);
    } catch (e) {
      // Priorizar mensagem do servidor (ex: "Extensão não permitida", "Sem permissão")
      // Fallback para e.message quando não há resposta HTTP (Network Error, timeout).
      const serverMsg = e.response?.data?.error || e.response?.data?.detail;
      const userMsg = serverMsg || (e.message === 'Network Error'
        ? 'Erro de rede. Verifique sua conexão e tente novamente.'
        : e.message || 'Tente novamente.');
      alert('Erro ao enviar arquivo assinado: ' + userMsg);
    } finally { setUploadingSignedId(null); }
  };

  const getStatusBadge = (l) => {
    if (docType === 'proposta') {
      return l.proposta_gerada
        ? <a href={l.proposta_pdf_url} target="_blank" rel="noopener noreferrer" style={{ display:'flex', alignItems:'center', gap:5, color:'#4ade80', fontSize:12, textDecoration:'none' }}><CheckCircle size={13} /> Tem proposta</a>
        : <span style={{ display:'flex', alignItems:'center', gap:5, color:'#475569', fontSize:12 }}><Clock size={13} /> Sem proposta</span>;
    }
    const gen = docType === 'cemig' ? l.cemig_gerado : l.contrato_gerado;
    const pdfUrl = docType === 'cemig' ? l.cemig_pdf_url : l.contrato_pdf_url;
    if (gen && pdfUrl) return <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ display:'flex', alignItems:'center', gap:5, color:'#4ade80', fontSize:12, textDecoration:'none' }}><CheckCircle size={13} /> Gerado</a>;
    if (gen) return <span style={{ display:'flex', alignItems:'center', gap:5, color:'#4ade80', fontSize:12 }}><CheckCircle size={13} /> Gerado</span>;
    return <span style={{ display:'flex', alignItems:'center', gap:5, color:'#475569', fontSize:12 }}><Clock size={13} /> Pendente</span>;
  };

  const getBtnLabel = (l, loading) => {
    if (loading) return 'Gerando...';
    if (docType === 'proposta') return l.proposta_gerada ? 'Regerar' : 'Gerar Proposta';
    if (docType === 'cemig') return l.cemig_gerado ? 'Regerar Procuração' : 'Gerar Procuração';
    return l.contrato_gerado ? 'Regerar Contrato' : 'Gerar Contrato';
  };

  return (
    <div style={{ marginBottom:10, background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflow:'hidden' }}>
      {propostaModal && (
        <PropostaModal
          lead={propostaModal}
          gerarUrl={gerarUrl}
          downloadPrefix={downloadPrefix}
          fetchLeads={fetchLeads}
          view={view}
          onClose={() => setPropostaModal(null)}
        />
      )}
      {kitContratoModal && (
        <EscolherKitContratoModal
          lead={kitContratoModal}
          gerando={gerandoId === kitContratoModal.lead_id}
          onConfirm={(kitId) => { gerar(kitContratoModal.lead_id, kitId); setKitContratoModal(null); }}
          onClose={() => setKitContratoModal(null)}
        />
      )}
      <button onClick={() => setSectionOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'16px 20px', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <span style={{ color:'#f6c90e', display:'flex' }}><Icon size={18} /></span>
        <span style={{ fontSize:15, fontWeight:700, color:'#e2e8f0', flex:1 }}>{title}</span>
        <span style={{ color:'#475569', display:'flex' }}>{sectionOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
      </button>
      {sectionOpen && (
        <div style={{ borderTop:'1px solid #1a2035', padding:'16px 20px' }}>
          <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'8px 12px', flex:1, maxWidth:300 }}>
              <Search size={14} color="#475569" />
              <input style={{ background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 }}
                placeholder="Buscar cliente..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {[{ v:'disponivel', l:'Disponível' }, { v:'fechadas', l:'Vendas Fechadas' }].map(opt => (
                <button key={opt.v} onClick={() => switchView(opt.v)} style={{
                  padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', border:'1px solid',
                  background: view === opt.v ? 'rgba(246,201,14,0.15)' : 'transparent',
                  color: view === opt.v ? '#f6c90e' : '#64748b',
                  borderColor: view === opt.v ? 'rgba(246,201,14,0.4)' : '#1a2035',
                }}>{opt.l}</button>
              ))}
            </div>
          </div>
          <div style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:10, overflowX:'auto', overflowY:'auto', maxHeight:400 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={th}>#</th><th style={th}>Cliente</th><th style={th}>Cidade</th>
                <th style={th}>Vendedor</th><th style={th}>Valor da Venda</th>
                <th style={th}>{view === 'fechadas' ? 'Data da Venda' : title}</th>
                {(docType === 'cemig' || docType === 'contrato') && <th style={th}>Assinado</th>}
                <th style={th}>Ação</th>
              </tr></thead>
              <tbody>
                {filtered.map(l => {
                  const loading = gerandoId === l.lead_id;
                  return (
                    <tr key={l.lead_id} onMouseEnter={e => e.currentTarget.style.background='#0e1420'} onMouseLeave={e => e.currentTarget.style.background=''}>
                      <td style={td}>#{l.lead_id}</td>
                      <td style={td}><button style={linkStyle} onClick={() => navigate('/jornada/' + l.cliente_id)}>{l.cliente}</button></td>
                      <td style={td}>{l.cidade}</td>
                      <td style={td}>{l.vendedor}</td>
                      <td style={td}>
                        {l.valor_total_venda != null
                          ? <span style={{ color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR', { minimumFractionDigits:2 })}</span>
                          : <span style={{ color:'#475569' }}>--</span>}
                      </td>
                      <td style={td}>
                        {view === 'fechadas' ? <span style={{ color:'#4ade80', fontWeight:600, fontSize:13 }}>{l.venda_fechada_em || '--'}</span> : getStatusBadge(l)}
                      </td>
                      {(docType === 'cemig' || docType === 'contrato') && (
                        <td style={td}>
                          {(() => {
                            const assinadoUrl = docType === 'cemig' ? l.cemig_assinado_url : l.contrato_assinado_url;
                            return assinadoUrl
                              ? <a href={assinadoUrl} target="_blank" rel="noopener noreferrer" style={{ color:'#4ade80', fontSize:12, fontWeight:600, textDecoration:'none' }}>✓ Assinado</a>
                              : <span style={{ color:'#475569', fontSize:12 }}>Não assinado</span>;
                          })()}
                        </td>
                      )}
                      <td style={td}>
                        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                          {view === 'fechadas' && docType === 'proposta' ? (
                            l.proposta_pdf_url
                              ? <a href={l.proposta_pdf_url} target="_blank" rel="noopener noreferrer" style={btn('#4ade80')}><FileDown size={13} /> Ver Proposta</a>
                              : <span style={{ fontSize:12, color:'#475569' }}>Sem proposta gerada</span>
                          ) : l.status === 'fechado' && docType === 'proposta' ? (
                            <span style={{ fontSize:12, color:'#475569' }}>Venda fechada</span>
                          ) : !readonly ? (
                            <button style={btn('#f6c90e', loading)} onClick={() => !loading && handleGerarClick(l)} disabled={loading}>
                              <FileDown size={13} />{getBtnLabel(l, loading)}
                            </button>
                          ) : null}
                          {(docType === 'cemig' || docType === 'contrato') && !readonly && (
                            <>
                              {/*
                                label htmlFor é mais confiável que button+.click() em iOS Safari.
                                O input oculto fica fora do fluxo visual; o label o ativa nativamente.
                                disabled={!!uploadingSignedId} impede abrir o seletor durante upload.
                              */}
                              <input
                                id={`signed-input-${l.lead_id}`}
                                type="file"
                                accept="application/pdf,image/*"
                                style={{ position:'absolute', width:0, height:0, opacity:0, overflow:'hidden' }}
                                disabled={!!uploadingSignedId}
                                onChange={e => { if (e.target.files[0]) { uploadSigned(l.lead_id, e.target.files[0]); e.target.value = ''; } }}
                              />
                              <label
                                htmlFor={`signed-input-${l.lead_id}`}
                                style={{
                                  ...btn('#60a5fa', uploadingSignedId === l.lead_id),
                                  pointerEvents: uploadingSignedId ? 'none' : 'auto',
                                }}
                              >
                                <Upload size={13} />{uploadingSignedId === l.lead_id ? 'Enviando...' : 'Subir Assinado'}
                              </label>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={(docType === 'cemig' || docType === 'contrato') ? 8 : 7} style={{ ...td, textAlign:'center', color:'#475569', padding:32 }}>Nenhum lead encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- FileSlot ---- */
function FileSlot({ label, currentUrl, onUpload, uploading, accept, readonly }) {
  const inputRef = useRef();
  const isImage = currentUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(currentUrl);
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
      <span style={{ width:20, flexShrink:0, color: currentUrl ? '#4ade80' : '#475569', display:'flex' }}>
        {currentUrl ? <CheckCircle size={16} /> : <Square size={16} />}
      </span>
      <span style={{ flex:1, fontSize:13, color: currentUrl ? '#e2e8f0' : '#64748b' }}>{label}</span>
      {currentUrl && (
        <a href={currentUrl} target="_blank" rel="noopener noreferrer" style={{ ...btn('#4ade80'), textDecoration:'none' }}>
          {isImage ? <Image size={13} /> : <File size={13} />} Ver
        </a>
      )}
      {!readonly && (
        <>
          <button style={btn('#f6c90e', uploading)} onClick={() => !uploading && inputRef.current.click()} disabled={uploading}>
            <Upload size={13} /> {uploading ? 'Enviando...' : currentUrl ? 'Substituir' : 'Enviar'}
          </button>
          <input ref={inputRef} type="file" accept={accept || 'image/*'} style={{ display:'none' }}
            onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }} />
        </>
      )}
    </div>
  );
}

/* ---- MultiFileSlot ---- */
function MultiFileSlot({ label, categoria, currentUrl, extraFiles, onUpload, onUploadExtra, onDeleteExtra, uploading, uploadingExtra, accept, readonly }) {
  const primaryRef = useRef();
  const extraRef   = useRef();
  const isImg = (u) => u && /\.(jpg|jpeg|png|gif|webp)$/i.test(u);
  return (
    <div style={{ paddingBottom:8, borderBottom:'1px solid #1a2035', marginBottom:4 }}>
      {/* Linha principal (arquivo original do model FotoCliente/DocumentoCliente) */}
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0' }}>
        <span style={{ width:20, flexShrink:0, color: currentUrl ? '#4ade80' : '#475569', display:'flex' }}>
          {currentUrl ? <CheckCircle size={16} /> : <Square size={16} />}
        </span>
        <span style={{ flex:1, fontSize:13, color: currentUrl ? '#e2e8f0' : '#64748b' }}>{label}</span>
        {currentUrl && (
          <a href={currentUrl} target="_blank" rel="noopener noreferrer" style={{ ...btn('#4ade80'), textDecoration:'none' }}>
            {isImg(currentUrl) ? <Image size={13} /> : <File size={13} />} Ver
          </a>
        )}
        {!readonly && (
          <>
            <button style={btn('#f6c90e', uploading)} onClick={() => !uploading && primaryRef.current.click()} disabled={uploading}>
              <Upload size={13} /> {uploading ? 'Enviando...' : currentUrl ? 'Substituir' : 'Enviar'}
            </button>
            <input ref={primaryRef} type="file" accept={accept || 'image/*'} style={{ display:'none' }}
              onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }} />
          </>
        )}
      </div>

      {/* Arquivos extras (ArquivoMultiplo) */}
      {extraFiles.map(f => (
        <div key={f.id} style={{ display:'flex', alignItems:'center', gap:8, marginLeft:32, marginBottom:5 }}>
          <span style={{ fontSize:12, color:'#64748b', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {isImg(f.url) ? <Image size={11} style={{ marginRight:4, display:'inline-block', verticalAlign:'middle' }} /> : <File size={11} style={{ marginRight:4, display:'inline-block', verticalAlign:'middle' }} />}
            {f.nome}
          </span>
          <a href={f.url} target="_blank" rel="noopener noreferrer" style={{ ...btn('#4ade80'), textDecoration:'none', fontSize:11, padding:'4px 9px' }}>Ver</a>
          {!readonly && (
            <button style={{ ...btn('#f87171'), fontSize:11, padding:'4px 9px' }} onClick={() => onDeleteExtra(f.id)}>
              <Trash2 size={11} /> Excluir
            </button>
          )}
        </div>
      ))}

    </div>
  );
}

/* ---- ClienteFileRow ---- */
function ClienteFileRow({ cliente, slots, uploadUrl, dataKey, onUpdated, withCpfRg, readonly }) {
  const [expanded, setExpanded] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [data, setData] = useState(cliente[dataKey] || {});
  const [cpfRgLocal, setCpfRgLocal] = useState({ cpf_doc: cliente.cpf_doc || '', rg_doc: cliente.rg_doc || '', email: cliente.email || '' });
  const [savingCpfRg, setSavingCpfRg] = useState(false);
  // Arquivos extras por categoria
  const [extraFiles, setExtraFiles] = useState({});    // { categoria: [{id,url,nome}] }
  const [extraLoaded, setExtraLoaded] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(null); // categoria

  // Carrega extras ao expandir (uma única vez)
  useEffect(() => {
    if (expanded && !extraLoaded) {
      api.get('/documentos/arquivo-multiplo/list/?cliente_id=' + cliente.cliente_id)
        .then(r => {
          const grouped = {};
          r.data.forEach(f => {
            if (!grouped[f.categoria]) grouped[f.categoria] = [];
            grouped[f.categoria].push(f);
          });
          setExtraFiles(grouped);
          setExtraLoaded(true);
        })
        .catch(() => setExtraLoaded(true));
    }
  }, [expanded, extraLoaded, cliente.cliente_id]);

  const saveCpfRg = async () => {
    setSavingCpfRg(true);
    try {
      await api.post('/documentos/salvar-cpf-rg/' + cliente.cliente_id + '/', cpfRgLocal);
    } catch (e) { alert('Erro ao salvar CPF/RG'); }
    finally { setSavingCpfRg(false); }
  };

  const upload = async (tipo, arquivo) => {
    setUploadingField(tipo);
    try {
      const form = new FormData();
      form.append('tipo', tipo);
      form.append('arquivo', arquivo);
      const resp = await api.post(uploadUrl + cliente.cliente_id + '/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setData(prev => ({ ...prev, [tipo]: resp.data.url }));
      if (onUpdated) onUpdated(cliente.cliente_id, tipo, resp.data.url);
    } catch (e) {
      alert('Erro ao enviar arquivo: ' + (e.message || 'tente novamente'));
    } finally { setUploadingField(null); }
  };

  const uploadExtra = async (categoria, arquivo) => {
    setUploadingExtra(categoria);
    try {
      const form = new FormData();
      form.append('arquivo', arquivo);
      form.append('categoria', categoria);
      form.append('cliente_id', cliente.cliente_id);
      const resp = await api.post('/documentos/arquivo-multiplo/upload/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const novo = { id: resp.data.id, url: resp.data.url, nome: arquivo.name };
      setExtraFiles(prev => ({ ...prev, [categoria]: [...(prev[categoria] || []), novo] }));
    } catch (e) {
      alert('Erro ao enviar: ' + (e.response?.data?.error || e.message));
    } finally { setUploadingExtra(null); }
  };

  const deleteExtra = async (categoria, arqId) => {
    if (!window.confirm('Excluir este arquivo?')) return;
    try {
      await api.delete('/documentos/arquivo-multiplo/' + arqId + '/delete/');
      setExtraFiles(prev => ({ ...prev, [categoria]: (prev[categoria] || []).filter(f => f.id !== arqId) }));
    } catch (e) { alert('Erro ao excluir: ' + (e.message || 'tente novamente')); }
  };

  const total = slots.length;
  const done  = slots.filter(s => data[s.field]).length;
  const extraCount = Object.values(extraFiles).reduce((a, arr) => a + arr.length, 0);

  return (
    <>
      <tr onClick={() => setExpanded(o => !o)} style={{ cursor:'pointer' }}
        onMouseEnter={e => e.currentTarget.style.background='#111827'}
        onMouseLeave={e => e.currentTarget.style.background=''}>
        <td style={td}><span style={{ color:'#475569', display:'inline-flex', marginRight:6 }}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span></td>
        <td style={td}><span style={{ color:'#e2e8f0', fontWeight:600 }}>{cliente.nome}</span></td>
        <td style={td}>{cliente.cidade}</td>
        <td style={td}>{cliente.vendedor}</td>
        <td style={td}>
          <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20,
            background: (done + extraCount) >= total ? 'rgba(74,222,128,0.12)' : 'rgba(100,116,139,0.12)',
            color: (done + extraCount) >= total ? '#4ade80' : '#64748b',
            border: '1px solid ' + ((done + extraCount) >= total ? 'rgba(74,222,128,0.3)' : 'rgba(100,116,139,0.2)') }}>
            {done + extraCount}/{total}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} style={{ background:'#080c14', padding:'0 24px 8px 52px', borderBottom:'1px solid #0f1117' }}>
            {slots.map(s => (
              <MultiFileSlot
                key={s.field}
                label={s.label}
                categoria={s.field}
                currentUrl={data[s.field]}
                extraFiles={extraFiles[s.field] || []}
                accept={s.accept || 'image/*'}
                uploading={uploadingField === s.field}
                uploadingExtra={uploadingExtra === s.field}
                onUpload={(file) => upload(s.field, file)}
                onUploadExtra={(file) => uploadExtra(s.field, file)}
                onDeleteExtra={(id) => deleteExtra(s.field, id)}
                readonly={readonly}
              />
            ))}
            {/* Documentos pessoais — múltiplos arquivos, upload livre por vendedor/chefe */}
            {(() => {
              const docPessoais = extraFiles['documento_pessoal'] || [];
              const uploadingDP = uploadingExtra === 'documento_pessoal';
              return (
                <div style={{ paddingBottom: 8, borderBottom: '1px solid #1a2035', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px 0' }}>
                    <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                      Documento pessoal do cliente
                    </span>
                    <span style={{ fontSize: 11, color: '#475569' }}>({docPessoais.length} arquivo{docPessoais.length !== 1 ? 's' : ''})</span>
                  </div>
                  {docPessoais.map(f => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 0, marginBottom: 5 }}>
                      <span style={{ fontSize: 12, color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <File size={11} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
                        {f.nome}
                      </span>
                      <a href={f.url} target="_blank" rel="noopener noreferrer" style={{ ...btn('#4ade80'), textDecoration: 'none', fontSize: 11, padding: '4px 9px' }}>Ver</a>
                      {!readonly && (
                        <button style={{ ...btn('#f87171'), fontSize: 11, padding: '4px 9px' }} onClick={() => deleteExtra('documento_pessoal', f.id)}>
                          <Trash2 size={11} /> Excluir
                        </button>
                      )}
                    </div>
                  ))}
                  {!readonly && (
                    <div style={{ marginTop: 4 }}>
                      <label style={{ ...btn('#a78bfa', uploadingDP), fontSize: 11, padding: '4px 10px', cursor: uploadingDP ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Plus size={11} /> {uploadingDP ? 'Enviando...' : 'Adicionar documento'}
                        <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={uploadingDP}
                          onChange={e => { if (e.target.files[0]) uploadExtra('documento_pessoal', e.target.files[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                  )}
                </div>
              );
            })()}
            {withCpfRg && (
              <div style={{ padding:'14px 0 6px 0' }}>
                <div style={{ display:'flex', alignItems:'flex-end', gap:12, flexWrap:'wrap', marginBottom:8 }}>
                  <div style={{ flex:1, minWidth:160 }}>
                    <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:5 }}>CPF (para Procuração)</div>
                    <input
                      style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:7, padding:'7px 10px', color:'#f6c90e', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' }}
                      placeholder="000.000.000-00"
                      value={cpfRgLocal.cpf_doc}
                      onChange={e => setCpfRgLocal(p => ({ ...p, cpf_doc: e.target.value }))}
                    />
                  </div>
                  <div style={{ flex:1, minWidth:160 }}>
                    <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:5 }}>RG (para Procuração)</div>
                    <input
                      style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:7, padding:'7px 10px', color:'#f6c90e', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' }}
                      placeholder="0000000"
                      value={cpfRgLocal.rg_doc}
                      onChange={e => setCpfRgLocal(p => ({ ...p, rg_doc: e.target.value }))}
                    />
                  </div>
                  <div style={{ flex:2, minWidth:220 }}>
                    <div style={{ fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:5 }}>E-mail do cliente</div>
                    <input
                      type="email"
                      style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:7, padding:'7px 10px', color:'#f6c90e', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' }}
                      placeholder="cliente@email.com"
                      value={cpfRgLocal.email}
                      onChange={e => setCpfRgLocal(p => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                  <button style={btn('#4ade80', savingCpfRg)} onClick={saveCpfRg} disabled={savingCpfRg}>
                    {savingCpfRg ? 'Salvando...' : 'Salvar dados'}
                  </button>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/* ---- ClienteFileSection ---- */
function ClienteFileSection({ title, icon: Icon, fetchUrl, uploadUrl, dataKey, slots, withCpfRg, readonly }) {
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);

  const fetchClientes = useCallback(() => {
    api.get(fetchUrl).then(r => setClientes(r.data)).catch(() => {});
  }, [fetchUrl]);

  useEffect(() => {
    if (sectionOpen && !loaded) { fetchClientes(); setLoaded(true); }
  }, [sectionOpen, loaded, fetchClientes]);

  const filtered = clientes.filter(c =>
    c.nome?.toLowerCase().includes(search.toLowerCase()) ||
    c.cidade?.toLowerCase().includes(search.toLowerCase())
  );

  const handleUpdated = (clienteId, campo, url) => {
    setClientes(prev => prev.map(c =>
      c.cliente_id === clienteId ? { ...c, [dataKey]: { ...c[dataKey], [campo]: url } } : c
    ));
  };

  return (
    <div style={{ marginBottom:10, background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflow:'hidden' }}>
      <button onClick={() => setSectionOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'16px 20px', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <span style={{ color:'#f6c90e', display:'flex' }}><Icon size={18} /></span>
        <span style={{ fontSize:15, fontWeight:700, color:'#e2e8f0', flex:1 }}>{title}</span>
        <span style={{ color:'#475569', display:'flex' }}>{sectionOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
      </button>
      {sectionOpen && (
        <div style={{ borderTop:'1px solid #1a2035', padding:'16px 20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'8px 12px', maxWidth:300, marginBottom:16 }}>
            <Search size={14} color="#475569" />
            <input style={{ background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 }}
              placeholder="Buscar cliente..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:10, overflowX:'auto', overflowY:'auto', maxHeight:400 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={{ ...th, width:32 }}></th>
                <th style={th}>Cliente</th><th style={th}>Cidade</th>
                <th style={th}>Vendedor</th><th style={th}>Arquivos</th>
              </tr></thead>
              <tbody>
                {filtered.map(c => (
                  <ClienteFileRow key={c.cliente_id} cliente={c} slots={slots} uploadUrl={uploadUrl} dataKey={dataKey} onUpdated={handleUpdated} withCpfRg={withCpfRg} readonly={readonly} />
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, textAlign:'center', color:'#475569', padding:32 }}>Nenhum cliente encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* EngenheiroRow and EngenheiroSection — UNUSED, kept for reference; moved to Clientes.js */
// eslint-disable-next-line no-unused-vars
function EngenheiroRowUnused({ cliente, onUpdated, readonly }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(cliente.engenheiro || {});
  const [uploading, setUploading] = useState(null);
  const [protocolo, setProtocolo] = useState(cliente.engenheiro?.protocolo_projeto || '');
  const [savingProto, setSavingProto] = useState(false);
  const [savingParecer, setSavingParecer] = useState(false);
  const [vistoriaNum, setVistoriaNum] = useState(cliente.engenheiro?.pedido_vistoria_numero || '');
  const [savingVistoriaNum, setSavingVistoriaNum] = useState(false);
  const [uploadingCrt, setUploadingCrt] = useState(false);
  const [crtFile, setCrtFile] = useState(null);
  const crtInputRef = useRef(null);
  const inputRef = useRef({});

  const FILE_SLOTS = [
    { field:'diagrama_unifilar', label:'Diagrama unifilar', accept:'image/*,application/pdf' },
    { field:'foto_debitos', label:'Foto comprovando debitos', accept:'image/*' },
  ];

  const uploadFile = async (tipo, arquivo) => {
    setUploading(tipo);
    try {
      const form = new FormData();
      form.append('tipo', tipo);
      form.append('arquivo', arquivo);
      const resp = await api.post('/documentos/upload-engenheiro/' + cliente.cliente_id + '/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setData(prev => ({ ...prev, [tipo]: resp.data.url }));
      if (onUpdated) onUpdated(cliente.cliente_id, tipo, resp.data.url);
    } catch (e) { alert('Erro ao enviar arquivo: ' + (e.message || 'tente novamente'));
    } finally { setUploading(null); }
  };

  const saveProtocolo = async () => {
    setSavingProto(true);
    try {
      const resp = await api.post('/documentos/protocolo-engenheiro/' + cliente.cliente_id + '/', { protocolo });
      setData(prev => ({ ...prev, protocolo_projeto: resp.data.protocolo }));
      if (onUpdated) onUpdated(cliente.cliente_id, 'protocolo_projeto', resp.data.protocolo);
    } catch { alert('Erro ao salvar protocolo.');
    } finally { setSavingProto(false); }
  };

  const saveParecer = async (status) => {
    setSavingParecer(true);
    try {
      const resp = await api.post('/documentos/parecer-status/' + cliente.cliente_id + '/', { status });
      setData(prev => ({ ...prev, parecer_acesso_status: resp.data.status, parecer_acesso_data: resp.data.data }));
    } catch (e) {
      const msg = e.response?.data?.error || 'Erro ao salvar parecer.';
      alert(msg);
    } finally { setSavingParecer(false); }
  };

  const fmtParecerData = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', timeZone:'America/Sao_Paulo' });
  };

  const saveVistoriaNumero = async () => {
    setSavingVistoriaNum(true);
    try {
      const resp = await api.post('/documentos/vistoria-numero/' + cliente.cliente_id + '/', { numero: vistoriaNum });
      setData(prev => ({ ...prev, pedido_vistoria_numero: resp.data.numero }));
      if (onUpdated) onUpdated(cliente.cliente_id, 'pedido_vistoria_numero', resp.data.numero);
    } catch { alert('Erro ao salvar número de vistoria.');
    } finally { setSavingVistoriaNum(false); }
  };

  const totalSlots = FILE_SLOTS.length + 3; // protocolo + parecer_acesso + pedido_vistoria
  const done = FILE_SLOTS.filter(s => data[s.field]).length
    + (data.protocolo_projeto ? 1 : 0)
    + (data.parecer_acesso ? 1 : 0)
    + (data.pedido_vistoria_numero ? 1 : 0);

  return (
    <>
      <tr onClick={() => setExpanded(o => !o)} style={{ cursor:'pointer' }}
        onMouseEnter={e => e.currentTarget.style.background='#111827'}
        onMouseLeave={e => e.currentTarget.style.background=''}>
        <td style={td}><span style={{ color:'#475569', display:'inline-flex', marginRight:6 }}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span></td>
        <td style={td}><span style={{ color:'#e2e8f0', fontWeight:600 }}>{cliente.nome}</span></td>
        <td style={td}>{cliente.cidade}</td>
        <td style={td}>
          <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20,
            background: done === totalSlots ? 'rgba(74,222,128,0.12)' : 'rgba(100,116,139,0.12)',
            color: done === totalSlots ? '#4ade80' : '#64748b',
            border: '1px solid ' + (done === totalSlots ? 'rgba(74,222,128,0.3)' : 'rgba(100,116,139,0.2)') }}>
            {done}/{totalSlots}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} style={{ background:'#080c14', padding:'0 24px 12px 52px', borderBottom:'1px solid #0f1117' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
              <span style={{ width:20, flexShrink:0, color: data.protocolo_projeto ? '#4ade80' : '#475569', display:'flex' }}>
                {data.protocolo_projeto ? <CheckCircle size={16} /> : <Square size={16} />}
              </span>
              <span style={{ fontSize:13, color: data.protocolo_projeto ? '#e2e8f0' : '#64748b', minWidth:180 }}>Protocolo do projeto</span>
              {readonly ? (
                <span style={{ fontSize:13, color:'#e2e8f0', flex:1 }}>{data.protocolo_projeto || '—'}</span>
              ) : (
                <>
                  <input type="text" value={protocolo} onChange={e => setProtocolo(e.target.value)} placeholder="Numero do protocolo"
                    style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:7, color:'#e2e8f0', fontSize:13, padding:'6px 10px', outline:'none', flex:1, maxWidth:220 }} />
                  <button style={btn(savingProto ? '#64748b' : '#f6c90e', savingProto)} onClick={saveProtocolo} disabled={savingProto}>
                    {savingProto ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
            </div>
            {/* ── Parecer de acesso (row especial) ── */}
            {(() => {
              const parecerUrl = data.parecer_acesso;
              const parecerStatus = data.parecer_acesso_status;
              const parecerData = data.parecer_acesso_data;
              const isImage = parecerUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(parecerUrl);
              return (
                <div style={{ padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
                  {/* linha do arquivo */}
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: (!parecerStatus && !readonly) ? 8 : 0 }}>
                    <span style={{ width:20, flexShrink:0, color: parecerUrl ? '#4ade80' : '#475569', display:'flex' }}>
                      {parecerUrl ? <CheckCircle size={16} /> : <Square size={16} />}
                    </span>
                    <span style={{ flex:1, fontSize:13, color: parecerUrl ? '#e2e8f0' : '#64748b' }}>Parecer de acesso</span>
                    {parecerUrl && (
                      <a href={parecerUrl} target="_blank" rel="noopener noreferrer" style={{ ...btn('#4ade80'), textDecoration:'none' }}>
                        {isImage ? <Image size={13} /> : <File size={13} />} Ver
                      </a>
                    )}
                    {!readonly && (
                      <>
                        <button style={btn('#f6c90e', uploading === 'parecer_acesso')} onClick={() => uploading !== 'parecer_acesso' && inputRef.current['parecer_acesso'].click()} disabled={uploading === 'parecer_acesso'}>
                          <Upload size={13} /> {uploading === 'parecer_acesso' ? 'Enviando...' : parecerUrl ? 'Substituir' : 'Enviar'}
                        </button>
                        <input ref={el => { inputRef.current['parecer_acesso'] = el; }} type="file" accept="image/*,application/pdf" style={{ display:'none' }}
                          onChange={e => { if (e.target.files[0]) uploadFile('parecer_acesso', e.target.files[0]); e.target.value = ''; }} />
                      </>
                    )}
                  </div>
                  {/* status do parecer */}
                  {parecerStatus ? (
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginLeft:32, marginTop:4 }}>
                      <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:20,
                        background: parecerStatus === 'aprovado' ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
                        color: parecerStatus === 'aprovado' ? '#4ade80' : '#f87171',
                        border: '1px solid ' + (parecerStatus === 'aprovado' ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)') }}>
                        {parecerStatus === 'aprovado' ? '✅ Aprovado' : '❌ Reprovado'}
                      </span>
                      {parecerData && (
                        <span style={{ fontSize:11, color:'#475569' }}>Data parecer: {fmtParecerData(parecerData)}</span>
                      )}
                    </div>
                  ) : !readonly ? (
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:32, marginTop:4 }}>
                      <span style={{ fontSize:12, color:'#64748b', marginRight:4 }}>Parecer:</span>
                      <button
                        style={{ ...btn('#4ade80'), opacity: savingParecer ? 0.6 : 1 }}
                        onClick={() => !savingParecer && saveParecer('aprovado')}
                        disabled={savingParecer}
                      >
                        ✅ Aprovado
                      </button>
                      <button
                        style={{ ...btn('#f87171'), opacity: savingParecer ? 0.6 : 1 }}
                        onClick={() => !savingParecer && saveParecer('reprovado')}
                        disabled={savingParecer}
                      >
                        ❌ Reprovado
                      </button>
                      {savingParecer && <span style={{ fontSize:12, color:'#64748b' }}>Salvando...</span>}
                    </div>
                  ) : null}
                </div>
              );
            })()}

            {/* ── outros arquivos ── */}
            {FILE_SLOTS.map(s => {
              const isImage = data[s.field] && /\.(jpg|jpeg|png|gif|webp)$/i.test(data[s.field]);
              return (
                <div key={s.field} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
                  <span style={{ width:20, flexShrink:0, color: data[s.field] ? '#4ade80' : '#475569', display:'flex' }}>
                    {data[s.field] ? <CheckCircle size={16} /> : <Square size={16} />}
                  </span>
                  <span style={{ flex:1, fontSize:13, color: data[s.field] ? '#e2e8f0' : '#64748b' }}>{s.label}</span>
                  {data[s.field] && (
                    <a href={data[s.field]} target="_blank" rel="noopener noreferrer" style={{ ...btn('#4ade80'), textDecoration:'none' }}>
                      {isImage ? <Image size={13} /> : <File size={13} />} Ver
                    </a>
                  )}
                  {!readonly && (
                    <>
                      <button style={btn('#f6c90e', uploading === s.field)} onClick={() => uploading !== s.field && inputRef.current[s.field].click()} disabled={uploading === s.field}>
                        <Upload size={13} /> {uploading === s.field ? 'Enviando...' : data[s.field] ? 'Substituir' : 'Enviar'}
                      </button>
                      <input ref={el => { inputRef.current[s.field] = el; }} type="file" accept={s.accept} style={{ display:'none' }}
                        onChange={e => { if (e.target.files[0]) uploadFile(s.field, e.target.files[0]); e.target.value = ''; }} />
                    </>
                  )}
                </div>
              );
            })}
            {/* ── Pedido de Vistoria CEMIG ── */}
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
              <span style={{ width:20, flexShrink:0, color: data.pedido_vistoria_numero ? '#4ade80' : '#475569', display:'flex' }}>
                {data.pedido_vistoria_numero ? <CheckCircle size={16} /> : <Square size={16} />}
              </span>
              <span style={{ fontSize:13, color: data.pedido_vistoria_numero ? '#e2e8f0' : '#64748b', minWidth:180 }}>Nº Pedido de Vistoria</span>
              {readonly ? (
                <span style={{ fontSize:13, color:'#e2e8f0', flex:1 }}>{data.pedido_vistoria_numero || '—'}</span>
              ) : (
                <>
                  <input type="text" value={vistoriaNum} onChange={e => setVistoriaNum(e.target.value)} placeholder="Número do pedido"
                    style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:7, color:'#e2e8f0', fontSize:13, padding:'6px 10px', outline:'none', flex:1, maxWidth:220 }} />
                  <button
                    style={{ ...btn('#4ade80'), opacity: savingVistoriaNum ? 0.5 : 1 }}
                    onClick={saveVistoriaNumero}
                    disabled={savingVistoriaNum || vistoriaNum === (data.pedido_vistoria_numero || '')}
                  >
                    {savingVistoriaNum ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
            </div>
            {/* ── CRT ── */}
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0' }}>
              <span style={{ width:20, flexShrink:0, color: data.crt_url ? '#4ade80' : '#475569', display:'flex' }}>
                {data.crt_url ? <CheckCircle size={16} /> : <Square size={16} />}
              </span>
              <span style={{ flex:1, fontSize:13, color: data.crt_url ? '#e2e8f0' : '#64748b' }}>CRT</span>
              {data.crt_url && (
                <a href={data.crt_url} target="_blank" rel="noopener noreferrer" style={{ ...btn('#4ade80'), textDecoration:'none' }}>
                  <File size={13} /> Ver CRT
                </a>
              )}
              {!data.crt_url && (
                <>
                  <input
                    ref={crtInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display:'none' }}
                    onChange={e => { setCrtFile(e.target.files[0] || null); e.target.value = ''; }}
                  />
                  <button
                    style={{ ...btn('#f6c90e'), opacity: uploadingCrt ? 0.5 : 1 }}
                    disabled={uploadingCrt}
                    onClick={async () => {
                      if (!crtFile) { crtInputRef.current?.click(); return; }
                      setUploadingCrt(true);
                      try {
                        const fd = new FormData();
                        fd.append('arquivo', crtFile);
                        const r = await api.post(`/engenheiro/${data.id}/subir-crt/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setData(prev => ({ ...prev, crt_url: r.data.url }));
                        setCrtFile(null);
                      } catch(e) { alert('Erro ao enviar CRT: ' + (e.response?.data?.error || e.message || 'tente novamente'));
                      } finally { setUploadingCrt(false); }
                    }}
                  >
                    <Upload size={13} /> {uploadingCrt ? 'Enviando...' : crtFile ? `Enviar: ${crtFile.name.slice(0,20)}` : 'Anexar CRT'}
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ---- EngenheiroSection UNUSED (moved to Clientes.js) ---- */
// eslint-disable-next-line no-unused-vars
function EngenheiroSectionUnused({ readonly }) {
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchData = useCallback(() => {
    api.get('/documentos/clientes-engenheiro/').then(r => setClientes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && !loaded) { fetchData(); setLoaded(true); }
  }, [open, loaded, fetchData]);

  const filtered = clientes.filter(c =>
    c.nome?.toLowerCase().includes(search.toLowerCase()) ||
    c.cidade?.toLowerCase().includes(search.toLowerCase())
  );

  const handleUpdated = (clienteId, campo, valor) => {
    setClientes(prev => prev.map(c =>
      c.cliente_id === clienteId ? { ...c, engenheiro: { ...c.engenheiro, [campo]: valor } } : c
    ));
  };

  return (
    <div style={{ marginBottom:10, background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflow:'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'16px 20px', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <span style={{ color:'#f6c90e', display:'flex' }}><Cpu size={18} /></span>
        <span style={{ fontSize:15, fontWeight:700, color:'#e2e8f0', flex:1 }}>Dados do Engenheiro</span>
        <span style={{ color:'#475569', display:'flex' }}>{open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
      </button>
      {open && (
        <div style={{ borderTop:'1px solid #1a2035', padding:'16px 20px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, background:'#080c14', border:'1px solid #1a2035', borderRadius:8, padding:'8px 12px', maxWidth:300, marginBottom:16 }}>
            <Search size={14} color="#475569" />
            <input style={{ background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none', flex:1 }}
              placeholder="Buscar cliente..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ background:'#080c14', border:'1px solid #1a2035', borderRadius:10, overflowX:'auto', overflowY:'auto', maxHeight:400 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={{ ...th, width:32 }}></th>
                <th style={th}>Cliente</th><th style={th}>Cidade</th><th style={th}>Dados</th>
              </tr></thead>
              <tbody>
                {filtered.map(c => <EngenheiroRow key={c.cliente_id} cliente={c} onUpdated={handleUpdated} readonly={readonly} />)}
                {filtered.length === 0 && (
                  <tr><td colSpan={4} style={{ ...td, textAlign:'center', color:'#475569', padding:32 }}>Nenhum cliente encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Main page ---- */
const FOTO_SLOTS = [
  { field:'foto_padrao', label:'Foto padrao', accept:'image/*' },
  { field:'foto_disjuntor', label:'Foto disjuntor', accept:'image/*' },
  { field:'foto_medidor', label:'Foto medidor', accept:'image/*' },
  { field:'foto_estrutura_telhado', label:'Foto estrutura telhado', accept:'image/*' },
  { field:'foto_frente_imovel', label:'Foto frente imovel', accept:'image/*' },
  { field:'foto_vistoria_tecnica', label:'Foto vistoria tecnica inicial', accept:'image/*' },
];

const DOC_SLOTS = [
  { field:'conta_cemig', label:'Conta CEMIG', accept:'image/*,application/pdf' },
];

export default function Documentos() {
  const { isChefe, isGerenteComercial, isEngenheiro, isPosVenda } = useAuth();
const docView = true;
  const docReadonly = (isEngenheiro() || isPosVenda()) && !isChefe() && !isGerenteComercial();
  const [modeloModal, setModeloModal] = useState(false);

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
        <div style={{ fontSize:22, fontWeight:700, color:'#f6c90e' }}>Documentos</div>
        {(isChefe() || isGerenteComercial()) && (
          <button onClick={() => setModeloModal(true)}
            style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:9, background:'rgba(246,201,14,0.12)', color:'#f6c90e', border:'1px solid rgba(246,201,14,0.35)', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <Settings size={15} /> Modelo Proposta
          </button>
        )}
      </div>
      <div style={{ fontSize:13, color:'#475569', marginBottom:24 }}>Gere e gerencie todos os documentos dos seus leads e clientes</div>

      {modeloModal && <ModeloPropostaModal onClose={() => setModeloModal(false)} />}

      {docView && <>
        <DocSection title="Proposta" icon={FileText} fetchUrl="/documentos/leads-propostas/" gerarUrl="/documentos/gerar-proposta/" downloadPrefix="proposta" docType="proposta" readonly={docReadonly} />
        <DocSection title="Procuração" icon={File} fetchUrl="/documentos/leads-cemig/" gerarUrl="/documentos/gerar-cemig-lead/" downloadPrefix="cemig" docType="cemig" readonly={docReadonly} />
        <DocSection title="Contrato" icon={ScrollText} fetchUrl="/documentos/leads-contrato/" gerarUrl="/documentos/gerar-contrato/" downloadPrefix="contrato" docType="contrato" readonly={docReadonly} />
      </>}

      {docView && <>
        <ClienteFileSection title="Fotos do Cliente" icon={Camera} fetchUrl="/documentos/clientes-fotos/" uploadUrl="/documentos/upload-foto/" dataKey="fotos" slots={FOTO_SLOTS} readonly={docReadonly} />
        <ClienteFileSection title="Documentos do Cliente" icon={FolderOpen} fetchUrl="/documentos/clientes-documentos/" uploadUrl="/documentos/upload-documento/" dataKey="documentos" slots={DOC_SLOTS} withCpfRg={!docReadonly} readonly={docReadonly} />
      </>}

      {/* Dados do Engenheiro movido para Clientes.js (DadosEngenheiroPanel) */}
    </div>
  );
}
